/**
 * W2.5 OSNAP-OVERRIDE-MODES-S2 — the interactive pick-loop module.
 *
 * Extracted from `stageInteractions.ts` (was 1963 lines, at the project's
 * 2000-line file-size ceiling — CLAUDE.md "Контроль размера файлов"). This is
 * the first slice to establish a dedicated "pick-loop module" seam; a later
 * slice (OTRACK-SUBSYSTEM) is expected to extend it, so it is factored as a
 * reusable `createInteractivePickLoop(bindings)` factory rather than a
 * one-off patch. Pure no-behavior-change move for everything EXCEPT the new
 * extended-override click-capture logic at the bottom of this file.
 *
 * # What this module owns
 *
 *   - The per-move OSNAP + hover resolution pipeline (`resolveAndCacheSnap`,
 *     `resolvePointerSnapAt`, `resolveInputSpaceAt`) and its side effects
 *     (overlay marker/tip, SELECTIONCYCLING store sync, hover emit, the
 *     read-only E2E `window.__edit_dwg_lasthover` mirror).
 *   - The snap cache (`lastSnappedWorld` / `lastResolvedSpaceId`) + the last
 *     cursor client position (`lastMoveClient`) + the last hover entity id
 *     (`lastHoverEntityId`) — FULLY encapsulated; `stageInteractions.ts` has
 *     zero direct reads/writes of these after the extraction.
 *   - The LMB click → commit-a-draft-point path (`commitClick`), including
 *     the drag-vs-click DISCRIMINATION-FREE commit (the caller determines
 *     `wasClick` and only calls `commitClick` when true — see
 *     `stageInteractions.onPointerUp`).
 *   - `handlePointerLeave` — the snap-cache teardown + the MC4 edge-case-#2
 *     hover-clear gating that used to live inline in `onPointerLeave`.
 *
 * # New in W2.5 — Extended (Apparent) Intersection override click-capture
 *
 * PR #501 shipped the RESOLVER for `extint`/`extappint`
 * (`snap/temporaryOverride.ts`) plus the sysvar store field
 * (`dynamicInput/sysvarStore.ts` `osnapExtendedOverride` /
 * `setOsnapExtendedOverride`), but `setOsnapExtendedOverride` had ZERO
 * callers — nothing could ever ARM the override, CAPTURE the first operand
 * on a click, or CLEAR it after the second click commits (AutoCAD one-shot
 * semantics: the override does not persist to the next pick). This module
 * adds that glue at the ONE choke point every drafting click already funnels
 * through (`commitClick`), so it is by-construction available on the FIRST
 * point of a brand-new drafting command too — `tryCaptureExtendedOverrideOperand`
 * reads only the live `osnapExtendedOverride` store state, never
 * `draftActiveRef` (which means something unrelated — VP-edge routing for the
 * rubber band — and is `false` for exactly the first-point case this slice
 * must support; see `StageInteractionBindings.draftActiveRef`'s doc).
 *
 * Does NOT touch `resolveExtendedOverride` / `resolveObjectSnap.ts` / the
 * live snap pipeline — those already resolve + preview the crossing on every
 * pointer move once `firstOwnerId` is captured; this module only wires the
 * click-driven arm/capture/clear.
 *
 * # DRAFT-DOCUMENT-FRAME — this module owns the coordinate-frame boundary
 *
 * `stageCameraMath.clientToWorld` unprojects against the OFFSET-world render
 * camera, so EVERY coordinate this module computes internally — the snap
 * cache, the OSNAP resolve, the overlay marker/guide projections, the OTRACK
 * acquired-point store, the `window.__edit_dwg_lasthover` mirror — is in the
 * renderer's offset-world frame (or, inside an eligible viewport, in that
 * VP's document-frame model space; `ActiveInputSpace` knows which).
 *
 * Exactly TWO coordinates leave for the application: the `onCanvasHover`
 * emit and the `onCanvasPoint` commit. Both are converted to the DOCUMENT
 * (AutoCAD/DXF) frame through `ActiveInputSpace.toDocument`, because what
 * they feed — the draft state machine, its `EntityMutation`, the saved DXF,
 * the Dynamic Input readout — is document-frame. The inverse
 * (`fromDocument`) is applied to the ONE value travelling the other way, the
 * ORTHO/POLAR anchor. Nothing else in this file changes frame.
 */

import * as THREE from "three";

// W2.5 — import the binding contract from the leaf `stageInteractionTypes.ts`
// module, NOT from `../stageInteractions` (which imports `createInteractivePickLoop`
// FROM this file to install it — a two-way edge would be a real import cycle,
// flagged by the project's zero-cycle dep-graph gate even though this specific
// import is type-only). `stageInteractions.ts` re-exports the same type
// verbatim, so callers there see no path change.
import type { StageInteractionBindings } from "../stageInteractionTypes";
import type { ActiveInputSpace } from "../viewports/activeInputSpace";
import { resolveInputSpaceForPointer } from "../snap/inputSpaceResolve";
import { resolveAidedPointerSnap } from "../snap/aidedPointerSnap";
import { type DraftAidResult } from "../snap/draftAidConstraint";
import {
  type PointerSnapStepResult,
  type OsnapBindings,
} from "../snap/pointerSnapStep";
import { entityCanProduceIntersectShape } from "../snap/osnapIntersections";
import { DRAG_THRESHOLD_PX, clientToWorld } from "../stageCameraMath";
import { useSelectionCyclingStore } from "../../state/selectionCyclingState";
import {
  useSysvarStore,
  selectEffectiveOtrack,
  selectOtrackShiftToAcquire,
} from "../../dynamicInput/sysvarStore";
import { useOtrackAcquisitionStore } from "../../state/otrackAcquisitionStore";
import { unapplyOffsetToPoint2d } from "../coordinateOrigin";
import { OTRACK_DWELL_MS } from "../snap/otrackDraftLock";
import { isOtrackShiftHeld } from "../snap/otrackShiftTracker";

/** Handle returned by {@link createInteractivePickLoop}. */
export interface PickLoopHandle {
  /**
   * Resolve OSNAP + hover for one pointer sample and apply every side
   * effect (overlay marker/tip, snap cache, cycling store, hover emit, E2E
   * mirror). Called from `onPointerMove` AND (indirectly, via
   * {@link refreshSnapAtLastPointer}) on an OSNAP-bitmask change with no move.
   */
  readonly resolveAndCacheSnap: (
    clientX: number,
    clientY: number,
    ortho: THREE.OrthographicCamera,
  ) => void;
  /**
   * Re-run the snap resolution at the LAST cursor position — same external
   * contract as `StageInteractionsHandle.refreshSnapAtLastPointer`.
   */
  readonly refreshSnapAtLastPointer: () => void;
  /** Invalidate the cached snap so the NEXT click recomputes under the moved camera. */
  readonly invalidateSnapCacheForPan: () => void;
  /**
   * LMB click → commit a drafting point. The caller (`stageInteractions.
   * onPointerUp`) has already determined this IS a true click (not a drag)
   * via `leftDragState`/`DRAG_THRESHOLD_PX` before calling this.
   */
  readonly commitClick: (clientX: number, clientY: number) => void;
  /** `onPointerLeave`'s snap-cache teardown + hover-clear gating. */
  readonly handlePointerLeave: () => void;
  /**
   * OTRACK-SUBSYSTEM — cancel any pending hover-dwell acquisition timer.
   * `handlePointerLeave` already covers the common "cursor left the canvas"
   * case, but a full unmount/reinstall (`stageInteractions.ts`'s own
   * `dispose()`) can happen WITHOUT a preceding pointerleave — without this,
   * a `setTimeout` armed just before unmount would still fire later and
   * silently mutate the (module-level, unmount-independent)
   * `useOtrackAcquisitionStore`. Callers that install this pick loop MUST
   * call this from their own teardown.
   */
  readonly dispose: () => void;
}

/**
 * Build the interactive pick-loop for one `installStageInteractions` call.
 * Framework-agnostic (no React) — mirrors `installStageInteractions` itself.
 */
export function createInteractivePickLoop(
  bindings: StageInteractionBindings,
): PickLoopHandle {
  const {
    canvas,
    cameraRef,
    sizeRef,
    invalidate,
    setOverlayState,
    onCanvasPoint,
    onCanvasHover,
    activeSpaceId,
    identityFallbackSpaceId,
    spatialIndexRef,
    osnapBindingsRef,
    hiddenLayersRef,
    viewportRedirectRef,
    draftActiveRef,
    draftPointPromptActiveRef,
    draftGenerationRef,
    orthoBasePointRef,
    polarReferenceAngleRef,
  } = bindings;

  let lastHoverEntityId: string | null = null;
  // W2.5 @codex P2 (round-8) — sibling cache of `lastHoverEntityId` that
  // holds `snap.rawHoverHitId` (the pure proximity pick, BEFORE a running
  // OSNAP + AUTOSNAP-magnet substitution replaces `hoverHitId` with the
  // snap target's owner). `tryCaptureExtendedOverrideOperand`'s own
  // last-resort fallback (camera not yet mounted) needs the entity actually
  // under the click, never the magnetized-to owner — see that function.
  let lastRawHoverEntityId: string | null = null;
  // Latest snap-resolved cursor world coord, refreshed on every
  // `resolveAndCacheSnap`. Read by `commitClick` so a true LMB click fires
  // `onCanvasPoint` with the exact OSNAP-magnet point the user saw at
  // release time (AutoCAD parity: "specify point" only commits on click).
  let lastSnappedWorld: { x: number; y: number } | null = null;
  // W2.5 @codex P2 (round-3) — whether the LAST resolved snap found a REAL
  // OSNAP candidate (`snap.snapHit != null`), as opposed to `lastSnappedWorld`
  // falling back to the raw/aid-locked cursor position with no hit. `commitClick`
  // needs this to distinguish a genuine extended-crossing resolution from a
  // second-operand MISS while an override is captured (see that function).
  let lastSnapHitPresent = false;
  // The resolved input-space id (`space.spaceId`) of the last move — "model"
  // inside the active VP, the layout id over paper outside it. Emitted with
  // `lastSnappedWorld` on the next click so the committed point is tagged
  // with the space it actually lives in.
  let lastResolvedSpaceId: string = activeSpaceId;
  // DRAFT-DOCUMENT-FRAME — the emit-space → DOCUMENT conversion that belongs
  // to `lastSnappedWorld`, cached alongside it so `commitClick` converts on
  // the branch the cached sample actually resolved (identity space ⇒
  // un-offset; redirected into a VP ⇒ already document). Seeded with the
  // identity-space conversion, matching `lastResolvedSpaceId`'s own seed:
  // before any move has been observed the pointer frame IS the offset-world
  // render frame. See `ActiveInputSpace.toDocument`.
  let lastToDocument: (pt: { x: number; y: number }) => { x: number; y: number } =
    unapplyOffsetToPoint2d;
  // OSNAP-TEMPOVERRIDE-WIRE — last ABSOLUTE cursor client position seen by a
  // real move, so `refreshSnapAtLastPointer()` can re-run the snap
  // resolution at the SAME spot when the effective OSNAP bitmask changes
  // WITHOUT a pointer move. Cleared on `handlePointerLeave`.
  let lastMoveClient: { x: number; y: number } | null = null;
  // OTRACK-SUBSYSTEM — hover-dwell acquisition timer state. `otrackDwellKey`
  // de-dupes so hovering the SAME candidate across several moves does not
  // re-arm the timer; changing OR losing the candidate cancels it.
  let otrackDwellTimer: ReturnType<typeof setTimeout> | null = null;
  let otrackDwellKey: string | null = null;

  // Duplicated from `stageInteractions.ts`'s own `getOrtho()` — that copy is
  // still needed there for the pan/wheel/zoom-picking paths that stay in the
  // host file; this tiny (camera-cast) helper is cheaper to duplicate than to
  // thread a getter/live-camera object through every pick-loop call site.
  function getOrtho(): THREE.OrthographicCamera | null {
    const cam = cameraRef.current as THREE.OrthographicCamera;
    return cam.isOrthographicCamera ? cam : null;
  }

  // Active input space for a paper-space point (paper→model inside the
  // active VP, identity outside). Thin closure over
  // `resolveInputSpaceForPointer` injecting the live viewport width /
  // redirect / fallback space id; see that module for the full paper-ppw +
  // fallback-scope contract.
  const resolveInputSpaceAt = (
    paperPt: { x: number; y: number },
    ortho: THREE.OrthographicCamera,
    ignoreCursorRect = false,
  ): ActiveInputSpace =>
    resolveInputSpaceForPointer({
      paperPt,
      ortho,
      ignoreCursorRect,
      viewportWidthPx: sizeRef.current.width,
      redirect: viewportRedirectRef?.current ?? null,
      fallbackSpaceId: identityFallbackSpaceId,
    });

  /**
   * PURE OSNAP + input-space + ORTHO/POLAR resolution for a pointer — NO
   * side effects. Shared by `resolveAndCacheSnap` (a real move) AND
   * `commitClick`'s no-prior-move fallback. The aid lock is gated on the
   * ACTUAL `snap.magnetized`, so a magnetised tap commits the snap point
   * (object snap OVERRIDES the aids). Being PURE lets the fallback read it
   * WITHOUT re-emitting `onCanvasHover`.
   *
   * MC4 edge case #2 (#234 @codex P2 follow-up) — when a draft session is in
   * progress (`draftActiveRef`) TWO spaces are resolved from the same
   * cursor: the GATED `space` feeds OSNAP/hover (never snaps to model
   * entities through paper outside the active VP frame), while the UNGATED
   * `draftSpace` maps the DRAFT POINT only (the rubber band spans past the
   * VP edge; the render-side clip trims it). Inside the rect both agree.
   */
  function resolvePointerSnapAt(
    clientX: number,
    clientY: number,
    ortho: THREE.OrthographicCamera,
    /**
     * W2.5 @codex P2 — OPTIONAL osnap bindings to resolve against INSTEAD OF
     * the live `osnapBindingsRef.current`. `osnapBindingsRef` is a
     * React-owned, `readonly current` ref refreshed only by
     * `useOsnapBindings`'s own `useMemo` reacting to a sysvar-store change —
     * that refresh happens on the NEXT render, not synchronously within the
     * SAME event-handler turn that just wrote the store. A caller that just
     * mutated the store (e.g. `tryCaptureExtendedOverrideOperand` capturing
     * the first operand) and wants an IMMEDIATE re-resolve to reflect that
     * write must pass the corrected bindings here — reading the ref would
     * still see the pre-write value. `undefined` (the default) reads the live
     * ref as before, unchanged for every other caller.
     */
    osnapOverride?: OsnapBindings | null,
    /**
     * W2.5 @codex round-11 P3 — forces the hover hit-test to run even when
     * no `onCanvasHover` callback is wired. `wantHover: !!onCanvasHover`
     * below is an OPTIMIZATION for the real-move/hover-highlight path
     * (skip the hit-test when nobody consumes it) — but
     * `tryCaptureExtendedOverrideOperand`'s fresh-resolve needs the SAME
     * hit-test for a completely different reason (finding the entity
     * under a CLICK, independent of hover-highlight wiring).
     * `onCanvasHover` is an OPTIONAL binding (`StageInteractionBindings
     * .onCanvasHover?:`) — a supported caller can wire `onCanvasPoint` +
     * OSNAP without it, and the Extended Intersection override must still
     * work for that caller. Default `false` keeps every other caller
     * (the real move handler, `commitClick`'s no-prior-move fallback)
     * byte-identical to the pre-existing `!!onCanvasHover` gate.
     */
    forceWantHover = false,
  ): {
    drafting: boolean;
    snap: PointerSnapStepResult;
    emitSpaceId: string;
    constrainedWorld: DraftAidResult["world"];
    polarGuide: DraftAidResult["polarGuide"];
    polarTip: DraftAidResult["polarTip"];
    otrackGuides: DraftAidResult["otrackGuides"];
    otrackTip: DraftAidResult["otrackTip"];
    viewportPresent: boolean;
    /**
     * OTRACK-SUBSYSTEM — the projector (`space.spaceId` → paper coords)
     * `resolveAndCacheSnap` uses to reproject the acquired-point STORE
     * (held in the same `space.spaceId` the OTRACK constraint math runs in)
     * into the paper-camera overlay markers. MUST mirror
     * `resolveAidedPointerSnap`'s own internal choice EXACTLY (local Codex
     * review round 2 P2 — "Project OTRACK markers from the active draft
     * space"): mid-draft with the cursor PAST the active VP edge
     * (`draftPastEdge`), the constraint math runs in `draftSpace`, not the
     * gated `space` — using `space.fromSpace` there reprojects a genuinely
     * `draftSpace`-space acquired point as if it were `space`-space,
     * jumping the persistent "+" markers to the wrong screen position.
     */
    fromSpace: (pt: { x: number; y: number }) => { x: number; y: number };
    /**
     * DRAFT-DOCUMENT-FRAME — the emit-space → DOCUMENT (AutoCAD/DXF)
     * conversion for THIS sample, taken from the SAME space `fromSpace` is
     * (see its note on `draftPastEdge`). `ActiveInputSpace.toDocument` owns
     * the branch logic: un-offset in the identity case (the pointer
     * unprojected against the OFFSET-world camera), identity when redirected
     * into a VP (`paperToModel` already returns a document model point).
     *
     * Applied ONLY where a coordinate leaves the pointer pipeline for the
     * application — the `onCanvasHover` emit and the `onCanvasPoint` commit.
     * Everything else in this module (snap cache, OSNAP resolve, overlay
     * marker projection, OTRACK acquired-point store) deliberately stays in
     * the offset-world/snap frame.
     */
    toDocument: (pt: { x: number; y: number }) => { x: number; y: number };
    /**
     * OTRACK-SUBSYSTEM — the exact inverse, for the one value that travels
     * the other way inside this loop: an acquired point read back out of
     * `useOtrackAcquisitionStore`, which is DOCUMENT-framed (see that
     * store's own frame note). Taken from the SAME (emit) space as
     * `toDocument` so the two can never disagree about which branch they
     * are on.
     */
    fromDocument: (pt: { x: number; y: number }) => { x: number; y: number };
  } {
    const rawWorld = clientToWorld(clientX, clientY, canvas, ortho);
    const drafting = draftActiveRef?.current === true;
    const space = resolveInputSpaceAt(rawWorld, ortho, false);
    const draftSpace = drafting
      ? resolveInputSpaceAt(rawWorld, ortho, true)
      : space;
    // Mirrors `resolveAidedPointerSnap`'s own `draftPastEdge` computation
    // exactly (same three operands) so the marker projector never disagrees
    // with the space the constraint math actually ran in.
    const draftPastEdge =
      drafting && draftSpace.viewport !== null && space.viewport === null;
    const emitSpace = draftPastEdge ? draftSpace : space;
    // DRAFT-DOCUMENT-FRAME — the ORTHO/POLAR anchor travels the OPPOSITE way
    // across the frame boundary the emit below crosses. `Scene3DStage`
    // derives it from the draft session's own points
    // (`orthoAnchorForDraftSessionWithFilter`), and the draft session works
    // in DOCUMENT coordinates, while `resolveDraftAidConstraint` compares it
    // against the emit-space cursor. `fromDocument` is the exact inverse of
    // the `toDocument` applied to the emit, taken from the SAME space, so
    // the two can never disagree about which branch they are on.
    const anchorDocument = orthoBasePointRef?.current ?? null;

    return {
      ...resolveAidedPointerSnap({
      rawWorld,
      cursorClient: { x: clientX, y: clientY },
      space,
      draftSpace,
      drafting,
      anchor: anchorDocument ? emitSpace.fromDocument(anchorDocument) : null,
      // N6 — POLAR relative-basis reference angle (last-segment direction).
      referenceAngleDeg: polarReferenceAngleRef?.current ?? null,
      paperOrigin: viewportRedirectRef?.current?.paperOrigin ?? null,
      osnap: osnapOverride !== undefined ? osnapOverride : (osnapBindingsRef?.current ?? null),
      spatialIndex: spatialIndexRef.current,
      dragThresholdPx: DRAG_THRESHOLD_PX,
      wantHover: forceWantHover || !!onCanvasHover,
      // Codex PR #784 round 4 F2 — read at event time (the ref is
      // republished by `LayerVisibilityEffect` on every LAYER change), so the
      // hover hit-test filters INSERT child geometry exactly like the click
      // path does.
      hiddenLayers: hiddenLayersRef?.current ?? null,
      }),
      fromSpace: emitSpace.fromSpace,
      toDocument: emitSpace.toDocument,
      fromDocument: emitSpace.fromDocument,
    };
  }

  /**
   * OSNAP-TEMPOVERRIDE-WIRE — runs the snap resolution then OWNS the side
   * effects it projects off that bundle: overlay marker, snap cache,
   * cycling store, hover emit, the read-only E2E mirror. Callable from BOTH
   * a real `onPointerMove` AND `refreshSnapAtLastPointer()` (re-resolve at
   * the same cursor when the effective OSNAP bitmask changes with no move).
   */
  function resolveAndCacheSnap(
    clientX: number,
    clientY: number,
    ortho: THREE.OrthographicCamera,
    /** W2.5 @codex P2 — forwarded verbatim to `resolvePointerSnapAt`; see its docs. */
    osnapOverride?: OsnapBindings | null,
  ): void {
    // Remember WHERE the cursor is (absolute client coords) so a later
    // OSNAP-bitmask change with NO pointer move can re-run the exact same
    // snap resolution at this spot (`refreshSnapAtLastPointer`). Every
    // caller of this function passes the just-moved-to coords, so this is
    // safe at the top regardless of which caller invoked it.
    lastMoveClient = { x: clientX, y: clientY };

    const {
      drafting,
      snap,
      emitSpaceId,
      constrainedWorld,
      polarGuide,
      polarTip,
      otrackGuides,
      otrackTip,
      viewportPresent,
      fromSpace,
      toDocument,
      fromDocument,
    } = resolvePointerSnapAt(clientX, clientY, ortho, osnapOverride);

    // marker / tip: paint when present, else clear unconditionally so the
    // marker hides when OSNAP is disabled or the magnet hits nothing.
    if (snap.snapTarget) {
      setOverlayState((prev) => ({
        ...prev,
        snapTarget: snap.snapTarget,
        snapTip: snap.snapTip,
      }));
    } else {
      setOverlayState((prev) =>
        prev.snapTarget == null && prev.snapTip == null
          ? prev
          : { ...prev, snapTarget: null, snapTip: null },
      );
    }
    // POLAR overlay guide line + tooltip; skip the write ONLY when BOTH the
    // incoming AND the previous state are fully idle (no line, no tip). N6
    // (local Codex review round 2 P2): a TRACKPATH-suppressed polar lock
    // produces `polarGuide == null` with a NON-null `polarTip` (TRACKPATH
    // gates the line only) — the guard must check the INCOMING `polarTip`
    // too, or a fresh engagement transitioning from idle straight into a
    // TRACKPATH-suppressed lock would skip the write and leave the tooltip
    // stuck null even though it should now show.
    setOverlayState((prev) =>
      polarGuide == null &&
      polarTip == null &&
      prev.polarGuide == null &&
      prev.polarTip == null
        ? prev
        : { ...prev, polarGuide, polarTip },
    );

    // OTRACK-SUBSYSTEM — alignment-path guide line(s) + tooltip (mirrors the
    // POLAR write above) PLUS the persistent acquired-point "+" markers,
    // reprojected from the store's `space.spaceId` coords into paper via the
    // GATED space's own `fromSpace` (same projection the guide endpoints
    // already went through inside `resolveDraftAidConstraint`).
    //
    // Local Codex review round 5 P2 ("acquired points need a spaceId") —
    // filtered to `spaceId === emitSpaceId` FIRST: a point acquired in a
    // DIFFERENT MSPACE_VIA_VP space (e.g. paper/layout, captured while
    // hovering outside the active viewport) is not a coordinate in THIS
    // space at all — reprojecting it through this space's `fromSpace` would
    // paint the marker at a bogus screen position. The point stays acquired
    // in the store (reappears once the cursor returns to its own space);
    // this filter is purely a display/consumption-time concern, mirroring
    // `otrackLockedCursor`'s own `currentSpaceId` filter for the alignment
    // math below.
    const acquiredPoints = useOtrackAcquisitionStore
      .getState()
      .acquiredPoints.filter((p) => p.spaceId === emitSpaceId);
    // `p.world` is DOCUMENT-framed (the store serves both writers), so it
    // crosses back into the emit space with that space's own `fromDocument`
    // BEFORE `fromSpace` (modelToPaper) projects it onto the paper overlay.
    // Identity branch → `fromDocument` re-applies the session offset, which
    // is exactly what the offset-world paper camera expects.
    const otrackMarkers =
      acquiredPoints.length > 0
        ? acquiredPoints.map((p) => fromSpace(fromDocument(p.world)))
        : null;
    setOverlayState((prev) =>
      (otrackGuides == null || otrackGuides.length === 0) &&
      otrackTip == null &&
      otrackMarkers == null &&
      (prev.otrackGuides == null || prev.otrackGuides.length === 0) &&
      prev.otrackTip == null &&
      prev.otrackMarkers == null
        ? prev
        : { ...prev, otrackGuides, otrackTip, otrackMarkers },
    );

    // OTRACK-SUBSYSTEM — hover-dwell acquisition. Reads `snap.snapHit`
    // (space.spaceId, the SAME space the constraint math above runs in) so a
    // pause over a LIVE OSNAP candidate arms a tracking point after
    // `OTRACK_DWELL_MS`. `otrackDwellKey` de-dupes across repeated moves
    // over the same candidate; changing/losing it cancels the pending timer.
    //
    // Local Codex review round 1 (3×P2), all fixed together — the three were
    // entangled in the same few lines:
    //   1. "Gate dwell acquisition to active point prompts" — an idle hover
    //      (no draft command at all) used to arm acquisition too; the
    //      acquired-point store only clears on draft END (never on draft
    //      START), so a stale idle acquisition silently affected a LATER,
    //      unrelated command's first point. Gated on `draftPointPromptActiveRef`
    //      (the BROADER "any command active" signal — NOT `draftActiveRef`,
    //      which stays false for an unanchored first point and would have
    //      broken OTRACK's own no-anchor-needed first-point engagement).
    //   2. "Don't consume the dwell key before Shift is held" — previously
    //      `otrackDwellKey` was updated to the new candidate EVEN when
    //      POLARMODE bit 8 (Shift-to-acquire) blocked arming a timer, so a
    //      LATER Shift-press while still hovering the same spot was
    //      indistinguishable from "already handled" and never (re)armed.
    //      Fixed by only updating the key once a timer is ACTUALLY armed (or
    //      the candidate itself is genuinely gone) — a Shift-gated candidate
    //      re-evaluates every move until Shift is pressed or it changes.
    //   3. "Cancel or revalidate delayed acquisitions" — the timer callback
    //      unconditionally acquired at fire time, even if OTRACKMODE was
    //      turned off, Shift was released, or the draft command ended
    //      DURING the dwell window (all of which can happen with no
    //      intervening pointer move, so the move-driven cancel above never
    //      sees them). The callback now revalidates every live gate before
    //      committing the acquisition.
    //
    // Round 2 (2×P2 — the round-1 fixes above introduced these):
    //   A. "Reset dwell key when a pending acquisition is rejected" — the
    //      round-1 finding-#3 fire-time revalidation REJECTS a stale
    //      acquisition (e.g. Shift released before the timer fired) but
    //      left `otrackDwellKey` set to that candidate, so a later retry
    //      (Shift pressed again) while STILL hovering the same spot, no
    //      intervening move, was indistinguishable from "already handled"
    //      — the exact finding-#2 symptom, just relocated to the
    //      fire-time rejection path instead of the arm-time one. Fixed by
    //      resetting the key on every rejection branch too (never on the
    //      success path, or a point sitting still right after acquisition
    //      would immediately re-arm and un-acquire itself on the next
    //      tick).
    //   B. "Project OTRACK markers from the active draft space" — see
    //      `resolvePointerSnapAt`'s own `fromSpace` doc above.
    const otrackSysvar = useSysvarStore.getState();
    const draftPointPromptActive = draftPointPromptActiveRef?.current === true;
    const otrackShiftGated = selectOtrackShiftToAcquire(otrackSysvar);
    // Local Codex review round 7 P2 ("Reset the dwell key across command
    // boundaries") — folds `draftGenerationRef` into the key itself. Without
    // this, re-hovering the SAME physical OSNAP candidate across a command
    // boundary (e.g. after `useOtrackAcquisitionEffects`'s round-7 clear
    // empties the store on a command hot-swap) produced the byte-identical
    // key as before the boundary — the arm branch's `dwellCandidateKey !==
    // otrackDwellKey` check stayed false forever, so NO fresh timer ever
    // re-armed and the user could not reacquire that point for the new
    // command without first moving off it and back.
    const dwellCandidateKey =
      draftPointPromptActive && selectEffectiveOtrack(otrackSysvar) && snap.snapHit
        ? `${draftGenerationRef?.current ?? 0}:${snap.snapHit.kind}:${snap.snapHit.ownerEntityId}:${Math.round(snap.snapHit.world.x * 1e4)}:${Math.round(snap.snapHit.world.y * 1e4)}`
        : null;
    if (dwellCandidateKey === null) {
      if (otrackDwellTimer !== null) {
        clearTimeout(otrackDwellTimer);
        otrackDwellTimer = null;
      }
      otrackDwellKey = null;
    } else if (dwellCandidateKey !== otrackDwellKey) {
      if (otrackDwellTimer !== null) {
        clearTimeout(otrackDwellTimer);
        otrackDwellTimer = null;
      }
      if (!otrackShiftGated || isOtrackShiftHeld()) {
        // Mark the key handled ONLY once a timer is actually armed — a
        // Shift-gated candidate with Shift NOT (yet) held leaves the key
        // unset so a later Shift-press this SAME move-less moment is still
        // re-evaluated on the next call (finding #2).
        otrackDwellKey = dwellCandidateKey;
        // `snap.snapHit.world` is in the ACTIVE SPACE (`pointerSnapStep`
        // maps the resolver's index-frame hit back with `fromOffsetWorld`);
        // the store is DOCUMENT-framed, so convert on the branch THIS sample
        // resolved. Captured at ARM time together with `emitSpaceId`, so the
        // pair can never desync (any move re-arms a fresh timer).
        const acquireAt = toDocument(snap.snapHit!.world);
        const armedForKey = dwellCandidateKey;
        // Local Codex review round 6 P2 ("Cancel dwell timers when the
        // draft prompt changes") — captured at ARM time, checked at FIRE
        // time below: `draftPointPromptActiveRef.current === true` alone
        // stays true across a command boundary crossed with no intervening
        // pointer move (end command A, start command B within
        // OTRACK_DWELL_MS), so it cannot by itself distinguish "still A's
        // prompt" from "a brand-new B's prompt that also happens to accept
        // a point". See `StageInteractionBindings.draftGenerationRef`'s doc.
        const armedForGeneration = draftGenerationRef?.current;
        otrackDwellTimer = setTimeout(() => {
          otrackDwellTimer = null;
          // Revalidate every live gate at FIRE time, not just at arm time
          // (round-1 finding #3) — any of these can flip during the dwell
          // window with no intervening pointer move.
          const liveSysvar = useSysvarStore.getState();
          const rejected =
            draftPointPromptActiveRef?.current !== true ||
            !selectEffectiveOtrack(liveSysvar) ||
            (selectOtrackShiftToAcquire(liveSysvar) && !isOtrackShiftHeld()) ||
            draftGenerationRef?.current !== armedForGeneration;
          if (rejected) {
            // Round-2 finding A — clear the key ONLY if it still points at
            // THIS rejected candidate (a move could have already re-keyed
            // it to something else in the meantime; never clobber that).
            if (otrackDwellKey === armedForKey) otrackDwellKey = null;
            return;
          }
          // Local Codex review round 5 P2 — `emitSpaceId` is captured from
          // THIS `resolveAndCacheSnap` call's own scope, matching
          // `acquireAt`'s space exactly (no intervening move can desync
          // them: any move re-evaluates `dwellCandidateKey` and re-arms a
          // fresh timer, cancelling this one — see the arm-time branch
          // above).
          useOtrackAcquisitionStore.getState().toggleAcquirePoint(acquireAt, emitSpaceId);
        }, OTRACK_DWELL_MS);
      }
    }

    // Cache the aid-constrained snapped world + space id for `commitClick`.
    // The cached world stays in the SNAP frame; `lastToDocument` carries the
    // matching emit-space → document conversion so a later click converts on
    // exactly the branch this sample resolved (DRAFT-DOCUMENT-FRAME).
    lastSnappedWorld = constrainedWorld;
    lastResolvedSpaceId = emitSpaceId;
    lastToDocument = toDocument;
    lastSnapHitPresent = snap.snapHit != null;

    // SELECTIONCYCLING badge: keep the stacked-candidates store in sync.
    const cyclingStore = useSelectionCyclingStore.getState();
    if (snap.stackedCandidates.length >= 2) {
      cyclingStore.setStackedCandidates(snap.stackedCandidates);
    } else if (cyclingStore.stackedCandidates.length > 0) {
      cyclingStore.setStackedCandidates([]);
    }

    // MC4 clip-debug — read-only E2E mirror of the resolver's decision (no
    // production code reads it; same hook pattern as `window.__edit_dwg_overlay`).
    // DRAFT-DOCUMENT-FRAME: deliberately mirrors the SNAP-frame
    // `constrainedWorld`, NOT the document-frame hover emit — its job is to
    // let a headless scenario compare against `__cam` / screen pixels, both
    // of which live in the offset-world render frame.
    if (typeof window !== "undefined") {
      (
        window as unknown as {
          __edit_dwg_lasthover?: {
            drafting: boolean;
            spaceId: string;
            x: number;
            y: number;
            viewportPresent: boolean;
          };
        }
      ).__edit_dwg_lasthover = {
        drafting,
        spaceId: emitSpaceId,
        x: constrainedWorld.x,
        y: constrainedWorld.y,
        viewportPresent,
      };
    }
    // W2.5 @codex P2 (round-8) — cache the PRE-substitution raw hover pick
    // unconditionally (not gated behind `onCanvasHover`, which is the
    // magnet-substituted hover-highlight consumer's own gate) so
    // `tryCaptureExtendedOverrideOperand`'s fallback stays in sync with
    // every real move regardless of whether hover highlighting is wired up.
    lastRawHoverEntityId = snap.rawHoverHitId;
    if (onCanvasHover) {
      if (snap.hoverHitId !== lastHoverEntityId) {
        lastHoverEntityId = snap.hoverHitId;
      }
      // DRAFT-DOCUMENT-FRAME — the hover emit LEAVES the pointer pipeline
      // (App's `handleStageHover` writes it to `session.hoverPoint`, and
      // `StageInteractionsBinder` also mirrors it onto the DI cursor
      // snapshot), so it is converted to the DOCUMENT frame here, exactly
      // like the click commit below. It must stay co-frame with
      // `session.points` or the rubber band and every relative DI readout
      // would be a whole session origin out.
      onCanvasHover(toDocument(constrainedWorld), emitSpaceId, snap.hoverHitId);
    }
  }

  /**
   * OSNAP-TEMPOVERRIDE-WIRE — re-run the snap resolution at the LAST cursor
   * position when nothing moved but the effective OSNAP bitmask changed.
   * Guarded by `lastMoveClient`: off-canvas ⇒ nothing to refresh.
   *
   * `osnapOverride` (W2.5 @codex P2) — forwarded verbatim to
   * `resolveAndCacheSnap`/`resolvePointerSnapAt`; see that function's docs.
   * Optional so the PUBLIC zero-arg contract (`StageInteractionsHandle.
   * refreshSnapAtLastPointer`, called by the host on an OSMODE/DSETTINGS
   * change) is unaffected — only `tryCaptureExtendedOverrideOperand`'s
   * internal call passes it.
   */
  function refreshSnapAtLastPointer(osnapOverride?: OsnapBindings | null): void {
    if (!lastMoveClient) return;
    const ortho = getOrtho();
    if (!ortho) return;
    resolveAndCacheSnap(lastMoveClient.x, lastMoveClient.y, ortho, osnapOverride);
    // The snap marker is a demand-mode WebGL overlay; a static mode-change
    // refresh has no other invalidation trigger, so force one repaint.
    invalidate();
  }

  /**
   * cmd-pan r6 P2-2 — invalidate the cached snap so the NEXT click
   * recomputes the snap under the current camera instead of committing the
   * pre-pan world point.
   *
   * W2.5 @codex P2 (round-6) — also reused (NOT pan-specific despite the
   * name) whenever a captured Extended-override commit consumes/clears the
   * override: `lastSnappedWorld`/`lastSnapHitPresent` would otherwise still
   * hold the JUST-COMMITTED override-resolved crossing, and a NEXT click
   * with no intervening move would silently re-commit that same stale
   * coordinate as the following draft point even though the override no
   * longer applies. Same underlying operation (drop a snap cache a
   * state-change outside a pointermove has invalidated) as the pan case —
   * one shared invalidator, not a second copy.
   */
  function invalidateSnapCacheForPan(): void {
    lastSnappedWorld = null;
    lastResolvedSpaceId = activeSpaceId;
    lastSnapHitPresent = false;
  }

  /**
   * W2.5 OSNAP-OVERRIDE-MODES-S2 — if the Extended (Apparent) Intersection
   * override is ARMED but its first operand is not yet captured
   * (`firstOwnerId === null`), THIS click is the operand-1 pick — never a
   * draft point. Deliberately reads ONLY the live sysvar store + the
   * pick-loop's own hover cache; agnostic to `draftActiveRef`/anchored state
   * by construction, so it works identically on the FIRST point of a
   * brand-new drafting command (AutoCAD: arm the override, pick object 1,
   * hover object 2, click to commit — all before/without any prior point).
   *
   * Returns `true` when the click is CONSUMED as an operand pick (hit or
   * miss — a miss just leaves the override armed for a retry, AutoCAD
   * "select first object" reprompt parity) so `commitClick` never falls
   * through to a draft-point emit for it.
   */
  function tryCaptureExtendedOverrideOperand(
    clientX: number,
    clientY: number,
  ): boolean {
    const store = useSysvarStore.getState();
    const ext = store.osnapExtendedOverride;
    if (!ext || ext.firstOwnerId !== null) return false;
    // W2.5 @codex P2 (round-4 + round-5) — ALWAYS resolve a FRESH hit-test at
    // the actual click position rather than trusting the `lastHoverEntityId`
    // cache. Two independent staleness classes both land on this same click:
    //   (round-4) the cache is null on an initial tap, and is CLEARED by
    //   `handlePointerLeave` when the cursor leaves the canvas — which
    //   happens for real when the user moves onto the Shift+right-click
    //   Object Snap menu popup (a DOM overlay outside the canvas) to click
    //   "Extended Intersection", then clicks straight back onto the canvas
    //   with no further move.
    //   (round-5) `handlePointerLeave` DELIBERATELY does NOT clear the cache
    //   while `draftActiveRef.current === true` (an anchored, mid-draft
    //   session — MC4 edge case #2, so a genuine mid-draft VP-edge exit
    //   doesn't nuke the rubber-band highlight). That preservation is correct
    //   for ITS purpose, but means the SAME popup round-trip during a LATER
    //   draft point leaves a STALE pre-menu entity id in the cache — a click
    //   at a DIFFERENT position (or empty space) would silently capture the
    //   wrong operand instead of falling through to a fresh resolve, because
    //   the cache was non-null (just stale, not absent).
    // Given the cache cannot be trusted as "accurate" vs. "stale-but-present"
    // from the operand-capture call site, always resolving fresh removes
    // BOTH staleness classes by construction — reuses the SAME one-truth
    // hover hit-test `resolveAndCacheSnap` itself uses
    // (`resolvePointerSnapAt`'s `snap.rawHoverHitId`), not a second,
    // driftable implementation. `lastRawHoverEntityId` is kept only as a
    // last-resort fallback for the (essentially never-hit) case where
    // `getOrtho()` itself is unavailable (camera not yet mounted).
    //
    // W2.5 @codex P2 (round-8) — read `rawHoverHitId`, NOT `hoverHitId`.
    // When a running OSNAP is active with AUTOSNAP magnet on,
    // `pointerSnapStep` REPLACES `hoverHitId` with the snap target's owner
    // (`snapHit.ownerEntityId`) — see that file's magnet-substitution
    // comment. Operand-1 selection must be a pure proximity pick of
    // whatever is actually under the click, unaffected by a running snap
    // magnetizing to a DIFFERENT nearby entity's endpoint/midpoint;
    // `rawHoverHitId` is the SAME distance-refined hit-test result BEFORE
    // that substitution runs.
    //
    // W2.5 @codex P3 (round-11) — pass `forceWantHover: true`. Without it,
    // `resolvePointerSnapAt`'s `wantHover: !!onCanvasHover` gate would skip
    // the ENTIRE hit-test (not just its callback emission) for any
    // consumer that wires `onCanvasPoint` + OSNAP without the OPTIONAL
    // `onCanvasHover` binding — silently breaking operand capture for a
    // type-supported configuration. Operand-1 needs the hit-test
    // regardless of whether hover-highlight wiring exists.
    const ortho = getOrtho();
    const hoverId = ortho
      ? resolvePointerSnapAt(clientX, clientY, ortho, undefined, true).snap
          .rawHoverHitId
      : lastRawHoverEntityId;
    const entity = hoverId
      ? osnapBindingsRef?.current?.getEntityById(hoverId)
      : undefined;
    if (hoverId && entity && entityCanProduceIntersectShape(entity)) {
      const captured = { mode: ext.mode, firstOwnerId: hoverId };
      store.setOsnapExtendedOverride(captured);
      // Re-resolve immediately at the same cursor spot so a second click
      // with NO intervening move doesn't commit the STALE pre-capture snap
      // (mirrors the OSNAP-TEMPOVERRIDE-WIRE stale-cache fix
      // `refreshSnapAtLastPointer` already exists for — same class of bug).
      //
      // W2.5 @codex P2 — `osnapBindingsRef.current` is a React-owned,
      // `readonly current` ref that `useOsnapBindings`'s own `useMemo`
      // refreshes on the NEXT render reacting to the SAME store write above —
      // NOT synchronously within this event-handler turn. Reading it here
      // (as `refreshSnapAtLastPointer()` with no argument would) still sees
      // the PRE-capture `extendedOverride` (firstOwnerId still null), so the
      // "immediate re-resolve" would silently no-op and a no-move second
      // click would commit the stale pre-capture point instead of the
      // extended crossing. Patch a LOCAL copy (never mutate the read-only
      // ref) with the just-captured override and pass it through explicitly.
      const liveBindings = osnapBindingsRef?.current;
      const patchedBindings = liveBindings
        ? { ...liveBindings, extendedOverride: captured }
        : liveBindings;
      refreshSnapAtLastPointer(patchedBindings);
    }
    // Miss (no entity under cursor, or entity can't participate) → stay
    // armed, `firstOwnerId` stays null, user tries again.
    return true;
  }

  /**
   * LMB click → commit a drafting point. Drag-vs-click discrimination
   * (`leftDragState`/`DRAG_THRESHOLD_PX`) stays in `stageInteractions.ts`'s
   * `onPointerUp` — this is only called once that already determined
   * `wasClick === true`.
   */
  function commitClick(clientX: number, clientY: number): void {
    if (tryCaptureExtendedOverrideOperand(clientX, clientY)) return;
    if (!onCanvasPoint) return;

    // W2.5 — a captured (uncommitted) Extended override resolves through the
    // SAME cached snap below (the live pipeline already supersedes running
    // snaps once `firstOwnerId` is set — see `resolveObjectSnap`). This
    // commit is the override's one-shot use: clear it right after so it does
    // NOT persist to the NEXT pick (AutoCAD parity).
    const ext = useSysvarStore.getState().osnapExtendedOverride;
    const overrideCaptured = ext != null && ext.firstOwnerId !== null;

    // Prefer the snapped world cached on the last move so the click commits
    // the OSNAP-magnet point, tagged with the resolved space id. Fall back to
    // a fresh resolve only if no move has happened yet (a tap after a focus
    // change, or the first click after a pan invalidated the cache).
    let clickWorld = lastSnappedWorld;
    let clickSpaceId = lastResolvedSpaceId;
    let hadSnapHit = lastSnapHitPresent;
    // DRAFT-DOCUMENT-FRAME — carried alongside the cached world so the
    // conversion below runs on the branch the CACHED sample resolved, not on
    // a freshly re-derived one.
    let clickToDocument = lastToDocument;
    if (!clickWorld) {
      const ortho = getOrtho();
      if (ortho) {
        const { constrainedWorld, emitSpaceId, snap, toDocument } =
          resolvePointerSnapAt(clientX, clientY, ortho);
        clickWorld = constrainedWorld;
        clickSpaceId = emitSpaceId;
        hadSnapHit = snap.snapHit != null;
        clickToDocument = toDocument;
      }
    }

    // W2.5 @codex P2 (round-3) — a CAPTURED override (waiting on the second
    // operand) that resolves to NO genuine snap hit (second object missed /
    // no valid crossing under the cursor) must NOT commit the raw/aid-locked
    // cursor position as a draft point. AutoCAD: the override's "select 2nd
    // object" sub-prompt stays active on a miss — swallow the click, leave
    // the override armed so the user can move and retry (mirrors
    // `tryCaptureExtendedOverrideOperand`'s own miss-handling for the FIRST
    // operand, just one step later in the same two-operand flow).
    if (overrideCaptured && !hadSnapHit) return;

    if (clickWorld) {
      // DRAFT-DOCUMENT-FRAME — THE write boundary. Everything upstream is
      // offset-world (`clientToWorld` unprojects against the offset-world
      // camera); everything downstream — the draft state machine, the
      // `EntityMutation` it commits, the saved DXF — is DOCUMENT. Converting
      // here, once, makes all 48 geometry commands `advanceDispatch.ts`
      // emits (plus the dimension / modify / block-authoring / VPCLIP
      // registries) correct by construction; converting per command would
      // make each one a fresh chance to corrupt a saved drawing.
      onCanvasPoint(clickToDocument(clickWorld), clickSpaceId);
      if (overrideCaptured) {
        useSysvarStore.getState().setOsnapExtendedOverride(null);
        // W2.5 @codex P2 (round-6) — drop the now-stale cached crossing so a
        // NEXT click with no intervening move re-resolves fresh (normal
        // running-snap / raw cursor) instead of silently re-committing the
        // JUST-CONSUMED override point a second time.
        invalidateSnapCacheForPan();
      }
    }
  }

  /**
   * `onPointerLeave`'s snap/hover teardown. MC4 edge case #2 — while a
   * draft is in progress, a `pointerleave` must NOT clear the draft's hover
   * point (AutoCAD floating-MSPACE parity: the rubber band FREEZES at its
   * last model position past the drawing-area edge, it does not vanish).
   * NOT drafting → the clear still fires so a stale entity-highlight / DI
   * coord does not linger after the cursor leaves.
   */
  function handlePointerLeave(): void {
    if (onCanvasHover && draftActiveRef?.current !== true) {
      onCanvasHover(null, activeSpaceId, null);
      lastHoverEntityId = null;
    }
    // W2.5 @codex P2 (round-8) — mirror `lastHoverEntityId`'s clear/preserve
    // rule for its raw sibling: NOT gated behind `onCanvasHover` (see the
    // unconditional cache write in `resolveAndCacheSnap`), but still
    // preserved mid-draft for the same MC4 edge-case-#2 reason.
    if (draftActiveRef?.current !== true) {
      lastRawHoverEntityId = null;
    }
    lastSnappedWorld = null;
    lastResolvedSpaceId = activeSpaceId;
    lastSnapHitPresent = false;
    // OSNAP-TEMPOVERRIDE-WIRE — cursor off the canvas: forget the last move
    // position so a mode change while the pointer is elsewhere does NOT
    // re-resolve a phantom snap at a stale spot.
    lastMoveClient = null;
    // OTRACK-SUBSYSTEM — cancel any pending dwell timer; the cursor left the
    // canvas so there is no candidate left to acquire.
    if (otrackDwellTimer !== null) {
      clearTimeout(otrackDwellTimer);
      otrackDwellTimer = null;
    }
    otrackDwellKey = null;
  }

  /**
   * OTRACK-SUBSYSTEM — see {@link PickLoopHandle.dispose}'s doc. Idempotent
   * (safe to call when no timer is pending).
   */
  function dispose(): void {
    if (otrackDwellTimer !== null) {
      clearTimeout(otrackDwellTimer);
      otrackDwellTimer = null;
    }
    otrackDwellKey = null;
  }

  return {
    resolveAndCacheSnap,
    refreshSnapAtLastPointer,
    invalidateSnapCacheForPan,
    commitClick,
    handlePointerLeave,
    dispose,
  };
}
